(ns app.tiktok-posting.page
  "Replicant page for publishing content to TikTok."
  (:require [parts.replicant.query :as query]
            [parts.replicant.command :as command]
            [ui.app-frame :as app-frame]))

(defn tiktok-publish-query
  [state]
  (let [eid (::story-media-eid state)]
    (when eid
      {:query/kind :query/tiktok-publish
       :query/refresh? true
       :query/data {:story-media/eid eid}})))

(defn- creator-info-query
  [state]
  (let [account-id (::selected-account-id state)]
    (when account-id
      {:query/kind :query/tiktok-creator-info
       :query/data {:tiktok.user/id account-id}})))

(defn- parse-float-value
  [value]
  (when (seq (str value))
    (let [n (js/parseFloat value)]
      (when-not (js/isNaN n)
        n))))

(defn- video-cover-timestamp-ms
  [state]
  (when-let [seconds (parse-float-value (or (not-empty (::video-cover-time-seconds state))
                                            "1"))]
    (max 0 (.round js/Math (* seconds 1000)))))

(defn- publish-command-data
  [state result]
  (let [account-id (::selected-account-id state)]
    (cond-> {:tiktok.user/id account-id
             :story-media/eid (:story-media/eid result)
             :blob-id (:story-media/blob-id result)
             :image-post? (:story-media/image-post? result)
             :title (::title state)
             :description (::description state)
             :privacy-level (::privacy-level state)
             :disable-comment? (::disable-comment state)
             :disable-duet? (::disable-duet state)
             :disable-stitch? (::disable-stitch state)
             :auto-audio? (::auto-audio state)
             :ai-generated? (::ai-generated state)
             :branded-content? (::branded-content state)
             :brand-organic? (::brand-organic state)}
      (not (:story-media/image-post? result))
      (assoc :video-cover-timestamp-ms (video-cover-timestamp-ms state)))))

(defn- schedule-command
  [state result date-ms]
  {:command/kind :command/tiktok-schedule
   :command/data (cond-> (publish-command-data state result)
                   date-ms
                   (assoc :tiktok.post/date date-ms))})

(defn- tiktok-icon []
  [:svg {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 448 512"
         :fill "currentColor" :class ["w-4" "h-4"]}
   [:path {:d "M448 209.9a210.1 210.1 0 01-122.8-39.3v178.8A162.6 162.6 0 11185 188.3v89.9a74.6 74.6 0 1052.2 71.2V0h88a121 121 0 00122.8 121.1z"}]])

(defn- preview-section
  [result]
  (let [preview-url (:story-media/preview-url result)
        video-url (:story-media/video-url result)
        image-post? (:story-media/image-post? result)]
    [:div
     {:class ["w-full" "sm:w-48" "md:w-56" "shrink-0"]}
     [:div
      {:class ["relative" "rounded-lg" "overflow-hidden" "bg-gray-900"
               "shadow-lg"]
       :style {:aspect-ratio "9/16"}}
      (if image-post?
        (when preview-url
          [:img {:src preview-url
                 :class ["w-full" "h-full" "object-cover"]}])
        (when video-url
          [:video {:src video-url
                   :poster preview-url
                   :controls true
                   :class ["w-full" "h-full" "object-cover"]}]))]]))

(defn- checkbox
  [{:keys [label description checked disabled store-key]}]
  [:label
   {:class (into ["flex" "items-start" "gap-3" "py-2" "cursor-pointer"]
                 (when disabled ["opacity-50" "cursor-not-allowed"]))}
   [:input
    {:type "checkbox"
     :checked (boolean checked)
     :disabled disabled
     :class ["mt-0.5" "h-4" "w-4" "rounded" "border-gray-300"
             "text-indigo-600" "cursor-pointer"]
     :on (when-not disabled
           {:click [[:store/assoc store-key (not checked)]]})}]
   [:div
    [:div {:class ["text-sm" "font-medium" "text-gray-900"]} label]
    (when description
      [:div {:class ["text-xs" "text-gray-500"]} description])]])

(defn- account-selector
  [state accounts]
  (let [selected-id (::selected-account-id state)]
    [:div
     {:class ["space-y-3"]}
     [:h3
      {:class ["text-sm" "font-semibold" "text-gray-700"]}
      "TikTok Account"]
     (if (seq accounts)
       [:div
        {:class ["space-y-2"]}
        (map
          (fn [account]
            (let [account-id (:tiktok.user/id account)
                  is-selected (= selected-id account-id)]
              [:div
               {:class (into ["flex" "items-center" "gap-3" "p-3" "rounded-lg" "border"
                              "cursor-pointer" "transition-colors"]
                             (if is-selected
                               ["border-gray-700" "bg-gray-50"]
                               ["border-gray-200" "bg-white" "hover:bg-gray-50"]))
                :replicant/key account-id
                :on {:click [[:store/assoc ::selected-account-id account-id]
                             [:store/assoc ::privacy-level nil]
                             [:data/query {:query/kind :query/tiktok-creator-info
                                           :query/data {:tiktok.user/id account-id}}]]}}
               [:div
                {:class ["w-10" "h-10" "rounded-full" "overflow-hidden" "bg-gray-200"
                         "shrink-0"]}
                (if (or (:tiktok.user/profile-picture-url account)
                        (:tiktok.user/profile-picture account))
                  [:img {:src (or (:tiktok.user/profile-picture-url account)
                                  (:tiktok.user/profile-picture account))
                         :class ["w-full" "h-full" "object-cover"]}]
                  [:div {:class ["w-full" "h-full" "flex" "items-center"
                                 "justify-center" "text-gray-400"]}
                   (tiktok-icon)])]
               [:div
                {:class ["flex-1" "min-w-0"]}
                [:div
                 {:class ["text-sm" "font-medium" "text-gray-900" "truncate"]}
                 (:tiktok.user/name account)]]
               [:div
                {:class (into ["w-5" "h-5" "rounded-full" "border-2" "flex" "items-center"
                               "justify-center" "shrink-0"]
                              (if is-selected
                                ["border-gray-700" "bg-gray-700"]
                                ["border-gray-300"]))}
                (when is-selected
                  [:svg {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 20 20"
                         :fill "white" :class "w-3 h-3"}
                   [:path {:fill-rule "evenodd" :clip-rule "evenodd"
                           :d "M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"}]])]]))
          accounts)]
       [:div
        {:class ["text-sm" "text-gray-500" "p-4" "text-center"
                 "bg-gray-50" "rounded-lg" "border" "border-gray-200"]}
        "No TikTok accounts connected."])
     [:a
      {:href "/ui/social-accounts"
       :class ["inline-flex" "items-center" "gap-1.5" "text-sm" "text-gray-700"
               "hover:text-gray-900" "font-medium"]}
      "+ Add account"]]))

(def privacy-level-labels
  {"SELF_ONLY" "Only You"
   "MUTUAL_FOLLOW_FRIENDS" "Friends"
   "FOLLOWER_OF_CREATOR" "Followers"
   "PUBLIC_TO_EVERYONE" "Everyone"})

(defn- date-ms
  [date-str]
  (when (seq date-str)
    (let [date-ms (.getTime (js/Date. date-str))]
      (when-not (js/isNaN date-ms)
        date-ms))))

(defn- future-date-ms
  [date-str]
  (when-let [date-ms (date-ms date-str)]
    (when (> date-ms (.getTime (js/Date.)))
      date-ms)))

(defn- current-datetime-local
  []
  (let [now (js/Date.)
        pad (fn [n] (if (< n 10) (str "0" n) (str n)))]
    (str (.getFullYear now) "-"
         (pad (inc (.getMonth now))) "-"
         (pad (.getDate now)) "T"
         (pad (.getHours now)) ":"
         (pad (.getMinutes now)))))

(defn- content-options
  [state result creator-info]
  (let [image-post? (:story-media/image-post? result)
        data (:tiktok/creator-info creator-info)]
    [:div
     {:class ["space-y-4"]}

     (when image-post?
       (let [title (or (::title state) "")]
         [:div
          {:class ["space-y-2"]}
          [:label
           {:class ["text-sm" "font-medium" "text-gray-700"]}
           "Title"]
          [:input
           {:type "text"
            :value title
            :placeholder "Title"
            :maxLength 90
            :class ["w-full" "px-3" "py-2" "text-sm" "border" "border-gray-300"
                    "rounded-lg" "focus:ring-2" "focus:ring-gray-500"
                    "focus:border-gray-500"]
            :on {:input [[:store/assoc ::title :event/target.value]]}}]
          [:p
           {:class ["text-xs" "text-gray-500"]}
           (str (count title) "/90 characters")]]))

     [:div
      {:class ["space-y-2"]}
      [:label
       {:class ["text-sm" "font-medium" "text-gray-700"]}
       (if image-post? "Description" "Caption")]
      [:textarea
       {:value (or (if image-post?
                     (::description state)
                     (::title state))
                   "")
        :placeholder "Add your description here with #hashtags ..."
        :rows 3
        :class ["w-full" "px-3" "py-2" "text-sm" "border" "border-gray-300"
                "rounded-lg" "focus:ring-2" "focus:ring-gray-500"
                "focus:border-gray-500" "resize-none"]
        :on {:input [[:store/assoc
                      (if image-post? ::description ::title)
                      :event/target.value]]}}]]

     (when (:tiktok/creator-info-error creator-info)
       [:p {:class ["text-xs" "text-red-600"]}
        "Storrito can no longer access this TikTok account. Please "
        [:a {:href "/ui/social-accounts"
             :class ["underline"]}
         "reconnect your TikTok account"]
        " and try again."])

     (when (seq (:privacy_level_options data))
       [:div
        {:class ["space-y-2"]}
        [:label
         {:class ["text-sm" "font-medium" "text-gray-700"]}
         "Who can view this post"]
        [:select
         {:required true
          :class ["w-full" "px-3" "py-2" "text-sm" "border" "border-gray-300"
                  "rounded-lg" "focus:ring-2" "focus:ring-gray-500"
                  "focus:border-gray-500"]
          :on {:change [[:store/assoc ::privacy-level :event/target.value]]}}
         [:option {:value ""
                   :disabled true
                   :selected (nil? (::privacy-level state))
                   :replicant/key "__placeholder"}
          "Choose who can view this post"]
         (map (fn [level]
                [:option {:value level
                          :selected (= level (::privacy-level state))
                          :replicant/key level}
                 (get privacy-level-labels level level)])
              (:privacy_level_options data))]])

     [:div
      {:class ["border-t" "border-gray-100" "pt-3" "space-y-1"]}
      (when image-post?
        (checkbox {:label "Auto Audio"
                   :description "Selects a random audio track"
                   :checked (::auto-audio state)
                   :store-key ::auto-audio}))
      (checkbox {:label "AI Generated"
                 :description "Label as AI-generated content"
                 :checked (::ai-generated state)
                 :store-key ::ai-generated})
      (checkbox {:label "Partnership"
                 :description "Paid partnership to promote a third-party business"
                 :checked (::branded-content state)
                 :store-key ::branded-content})
      (checkbox {:label "Branded Content"
                 :description "Promoting the creator's own business"
                 :checked (::brand-organic state)
                 :store-key ::brand-organic})]

     (when data
       [:div
        {:class ["border-t" "border-gray-100" "pt-3" "space-y-1"]}
        (checkbox {:label "Disable Comments"
                   :description "Disable comment function"
                   :checked (::disable-comment state)
                   :disabled (:comment_disabled data)
                   :store-key ::disable-comment})
        (when-not image-post?
          (checkbox {:label "Disable Duet"
                     :description "Prevent other users from making Duets"
                     :checked (::disable-duet state)
                     :disabled (:duet_disabled data)
                     :store-key ::disable-duet}))
        (when-not image-post?
          (checkbox {:label "Disable Stitch"
                     :description "Prevent other users from making Stitches"
                     :checked (::disable-stitch state)
                     :disabled (:stitch_disabled data)
                     :store-key ::disable-stitch}))])]))

(defn- publish-section
  [state result]
  (let [q (tiktok-publish-query state)
        date-str (or (not-empty (::schedule-date-str state))
                     (current-datetime-local))
        schedule-date-ms (future-date-ms date-str)
        schedule? (some? schedule-date-ms)
        post-date-ms (or schedule-date-ms
                         (date-ms date-str)
                         (.getTime (js/Date.)))
        cmd (schedule-command state result post-date-ms)
        queued (::queued state)
        scheduled (::scheduled state)
        can-publish (and (::selected-account-id state)
                         (::privacy-level state)
                         (not queued)
                         (not scheduled)
                         (or (:organization/positive-credit-balance? result)
                             (:organization/legacy-customer? result)))
        pricing (:tiktok/pricing result)]
    [:div
     {:class ["space-y-4" "border-t" "border-gray-100" "pt-4"]}

     [:div
      {:class ["space-y-2"]}
      [:label
       {:class ["text-sm" "font-medium" "text-gray-700"]}
       "Post now or schedule"]
      [:input
       {:type "datetime-local"
        :value (or date-str (current-datetime-local))
        :class ["w-full" "px-3" "py-2" "text-sm" "border" "border-gray-300"
                "rounded-lg" "focus:ring-2" "focus:ring-gray-500"
                "focus:border-gray-500"]
        :on {:input [[:store/assoc ::schedule-date-str :event/target.value]
                     [:store/assoc ::queued nil]
                     [:store/assoc ::scheduled nil]]}}]
      [:p
       {:class ["text-xs" "text-gray-500"]}
       "Choose a future date to schedule, or leave it as now to post immediately."]]

     (when queued
       [:div
        {:class ["p-3" "rounded-lg" "bg-green-50" "border" "border-green-200"]}
        [:p
         {:class ["text-sm" "text-green-800" "font-medium"]}
         "TikTok post queued. It will appear below while it publishes."]])

     (when scheduled
       [:div
        {:class ["p-3" "rounded-lg" "bg-green-50" "border" "border-green-200"]}
        [:p
         {:class ["text-sm" "text-green-800" "font-medium"]}
         "TikTok post scheduled successfully!"]])

     (when (command/error? state cmd)
       [:div
        {:class ["p-3" "rounded-lg" "bg-red-50" "border" "border-red-200"]}
        [:p
         {:class ["text-sm" "text-red-800"]}
         (or (command/get-latest-error state cmd)
             "Failed to queue TikTok post. Please try again.")]])

     (when (and (not (:organization/positive-credit-balance? result))
                (not (:organization/legacy-customer? result)))
       [:div
        {:class ["p-3" "rounded-lg" "bg-amber-50" "border" "border-amber-200"]}
        [:p
         {:class ["text-sm" "text-amber-800"]}
         (if (:organization/free-account? result)
           [:span "You need a subscription to publish. "
            [:a {:href "/ui/subscription"
                 :class ["font-medium" "text-amber-900" "underline"]}
             "Subscribe now"]]
           [:span "Your credit balance is empty. "
            [:button
             {:class ["font-medium" "text-amber-900" "underline" "cursor-pointer"]
              :on {:click [[:data/query {:query/kind :query/user-session}]
                           [:ui/modal-open :modals/credits-exhausted {}]]}}
             "Top up credits"]])]])

     [:button
      {:class (into ["w-full" "py-2.5" "px-4" "text-sm" "font-semibold" "rounded-lg"
                     "transition-colors" "cursor-pointer" "inline-flex" "items-center"
                     "justify-center" "gap-2"]
                    (if can-publish
                      ["bg-gray-900" "text-white" "hover:bg-gray-800"]
                      ["bg-gray-300" "text-gray-500" "cursor-not-allowed"]))
       :disabled (or (not can-publish)
                     (command/issued? state cmd))
       :on {:click (when can-publish
                     [[:data/command
                       cmd
                       {:on-success [(if schedule?
                                       [:store/assoc ::scheduled true]
                                       [:store/assoc ::queued true])
                                     [:data/query q]]
                        :on-error [[:ui/show-error]]}]])}}
      (tiktok-icon)
      (cond
        (command/issued? state cmd) (if schedule? "Scheduling..." "Queueing...")
        scheduled "Scheduled"
        queued "Queued for TikTok"
        schedule? "Schedule TikTok Post"
        :else "Post now")]

     (when (and can-publish
                (not (:organization/legacy-customer? result))
                pricing)
       (if-let [discount (:discount pricing)]
         [:div
          {:class ["flex" "items-center" "justify-center" "gap-2" "text-sm"]}
          [:span {:class ["text-gray-400" "line-through"]}
           (str "$" (:original-price discount))]
          [:span {:class ["font-semibold" "text-green-600"]}
           (str "$" (:discounted-price discount))]
          [:span {:class ["inline-flex" "items-center" "rounded-md" "bg-green-50"
                          "px-2" "py-0.5" "text-xs" "font-medium" "text-green-700"
                          "ring-1" "ring-inset" "ring-green-600/20"]}
           (str (.round js/Math (* (:discount-percentage discount) 100)) "% OFF")]]
         [:div
          {:class ["text-center" "text-sm" "text-gray-500"]}
          (str "Cost $" (:price pricing))]))

     [:div
      {:class ["text-center" "text-xs" "text-gray-400"]}
      [:p
       "By posting, you agree to TikTok's "
       [:a {:href "https://www.tiktok.com/legal/page/global/music-usage-confirmation/en"
            :target "_blank"
            :class ["underline"]}
        "Music Usage Confirmation"]
       " and "
       [:a {:href "https://www.tiktok.com/legal/page/global/bc-policy/en"
            :target "_blank"
            :class ["underline"]}
        "Branded Content Policy"]
       "."]]]))

(defn- format-scheduled-date
  [date]
  (when date
    (.toLocaleString (js/Date. date))))

(defn- scheduled-status-label
  [status]
  (case status
    :scheduled "Scheduled"
    :in-progress "Posting..."
    :executed "Posted"
    :error "Error"
    :canceled "Canceled"
    "Scheduled"))

(defn- scheduled-posts-section
  [state result]
  (let [q (tiktok-publish-query state)
        posts (:tiktok-posting/history result)]
    (when (seq posts)
      [:div
       {:class ["border-t" "border-gray-100" "pt-4" "space-y-3"]}
       [:h3
        {:class ["text-sm" "font-semibold" "text-gray-700"]}
        "TikTok posts"]
       (map
         (fn [post]
           (let [cancel-cmd {:command/kind :command/tiktok-post-cancel
                             :command/data {:db/id (:db/id post)}}
                 cancelable? (= :scheduled (:status post))]
             [:div
              {:class ["flex" "items-center" "justify-between" "gap-3" "p-3"
                       "rounded-lg" "border" "border-gray-200" "bg-white"]
               :replicant/key (:db/id post)}
              [:div
               {:class ["min-w-0"]}
               [:div
                {:class ["text-sm" "font-medium" "text-gray-900"]}
                (scheduled-status-label (:status post))]
               [:div
                {:class ["text-xs" "text-gray-500"]}
                (format-scheduled-date (:tiktok.post/date post))]
               (when-let [account-name (get-in post [:tiktok.post/account :tiktok.user/name])]
                 [:div
                  {:class ["text-xs" "text-gray-400" "truncate"]}
                  account-name])
               (when-let [error (:tiktok.post/error-message post)]
                 [:div
                  {:class ["text-xs" "text-red-600" "truncate"]
                   :title error}
                  error])]
              (when cancelable?
                [:button
                 {:class ["text-xs" "font-medium" "text-red-600" "hover:text-red-500"
                          "cursor-pointer" "shrink-0"]
                  :on {:click [[:js/confirm "Cancel this scheduled TikTok post?"]
                               [:data/command cancel-cmd
                                {:on-success [[:data/query q]]
                                 :on-error [[:ui/show-error]]}]]}}
                 "Cancel"])]))
         posts)])))

(defn render
  [state]
  (let [q (tiktok-publish-query state)
        result (when q (query/get-result state q))
        ciq (creator-info-query state)
        creator-info (when ciq (query/get-result state ciq))
        loading (or (not q)
                    (and (query/loading? state q)
                         (not (query/available? state q))))]
    (if loading
      [:div
       {:class ["flex" "items-center" "justify-center" "min-h-screen"]}
       [:div {:class ["text-gray-500"]} "Loading..."]]
      (app-frame/app-frame
        state
        [:div
         {:class ["max-w-4xl" "mx-auto" "overflow-x-hidden"]}
         [:div
          {:class ["mb-4"]}
          [:h1
           {:class ["text-xl" "font-bold" "text-gray-900"]}
           (or (:story-media/name result) "TikTok Publishing")]
          [:p
           {:class ["text-sm" "text-gray-500" "mt-1"]}
           [:a
            {:href "/ui/agenda"
             :class ["text-indigo-600" "hover:text-indigo-700"]}
            "Back to schedule"]]]
         [:div
          {:class ["flex" "flex-col" "sm:flex-row" "gap-6"]}
          (preview-section result)
          [:div
           {:class ["flex-1" "space-y-6"]}
           (account-selector state (:tiktok/accounts result))
           (when (::selected-account-id state)
             (if (and ciq (query/loading? state ciq)
                       (not (query/available? state ciq)))
               [:div
                {:class ["flex" "items-center" "justify-center" "py-8"]}
                [:div {:class ["text-gray-400" "text-sm"]} "Loading account info..."]]
               [:div
                {:class ["space-y-4"]}
                (content-options state result creator-info)
                (publish-section state result)
                (scheduled-posts-section state result)]))]]]))))

(defn on-load
  [location]
  (let [eid-str (get-in location [:location/query-params :story-eid])
        eid (when eid-str (js/parseInt eid-str 10))]
    (when eid
      [[:ui/hide-sidebar-on-mobile]
       [:store/assoc ::story-media-eid eid]
       [:store/assoc ::queued nil]
       [:store/assoc ::scheduled nil]
       [:store/assoc ::schedule-date-str nil]
       [:store/assoc ::video-cover-time-seconds "1"]
       [:data/query (tiktok-publish-query {::story-media-eid eid})]])))

(def page
  {:page-id :pages/tiktok-publish
   :route [["ui" "tiktok-publish"]]
   :on-load on-load
   :render #'render})
